import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { t } from 'i18next';
import TextInput from 'src/components/UI/input/TextInput';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import RadioInput from 'src/components/UI/input/RadioInput';
import ColorPicker from 'src/components/UI/input/ColorPicker';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import SelectInput from 'src/components/UI/input/SelectInput';
import { CONSTANTS } from 'src/components/questionnaire/Constants';
import { calculateAge, getHPOSearchURL } from 'src/util/util';
import {
  readTerminologyAvailability,
  terminologyAvailabilityFromError
} from 'src/util/terminologyAvailability';
import Alert from '@mui/material/Alert';
import { HPO_CONSTANTS } from 'src/components/Constants';
import axios from 'src/axios';
import { AppContext, SnackContext } from 'src/store/ContextStore';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import {
  leadingClassification,
  strictAcmgTier
} from 'src/util/variantPathogenicity';
import ClinicalNoteDeleteModal from './ClinicalNoteDeleteModal';
import {
  AFFECTED_STATUS,
  AFFECTED_STATUS_OPTIONS,
  TIER_TO_CLASSIFICATION,
  REPORTED_CLASSIFICATION_OPTIONS
} from './carrierStatusOptions';
import { isAnonymousOrLoading } from '../../../anonymousPedigrees';

const ButtonContainer = styled(Grid)({
  display: 'flex',
  gap: 10,
  justifyContent: 'center',
  marginTop: 5
});

const ProvenancePill = styled('span')(({ theme }) => ({
  display: 'inline-block',
  padding: '1px 8px',
  borderRadius: 10,
  fontSize: 12,
  backgroundColor: theme.palette.action.selected,
  color: theme.palette.text.secondary
}));

// Reserved for source:"inferred" unconfirmed obligate (Clinical-Insights-gated,
// later phase). Defined but intentionally unused this phase.
// eslint-disable-next-line no-unused-vars
const SuggestedChip = styled('span')(({ theme }) => ({
  display: 'inline-block',
  padding: '1px 8px',
  borderRadius: 10,
  fontSize: 12,
  border: `1px dashed ${theme.palette.text.disabled}`,
  color: theme.palette.text.secondary,
  '&::before': {
    content: '"\\2726 "'
  }
}));

// The person's other disease records are compared on code, not name: two codes
// for one condition (Marfan syndrome under OMIM and under ICD-10) are distinct
// records by design, and re-coding one onto the other's code is what must be
// blocked — it would leave the person with two identical records.
const sameCode = (a, b) => String(a ?? '') === String(b ?? '');

const EditDiagnoseModal = ({
  handleClose,
  onSubmit,
  formValues: initialFormValues,
  onDeleteDiagnose,
  isDisabled,
  patientDob,
  currentUserName,
  staffMemberNames = [],
  genotypes = [],
  siblingDiagnoses = []
}) => {
  const [formValues, setFormValues] = useState({
    ...initialFormValues,
    recordedBy: initialFormValues?.recordedBy || currentUserName || ''
  });
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // This modal was cloned from DiagnosesMenu with the banner JSX left behind,
  // so `isError` was set in the catch and read by nothing — a dead state, and
  // `no-unused-vars` could not see it because the success path also read it.
  // The modal could not report even the one failure mode its parent could
  // (TGCLOUD-1785).
  const [unavailable, setUnavailable] = useState(null);
  const [options, setOptions] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [page, setPage] = useState(HPO_CONSTANTS['initialPage']);
  const [open, setOpen] = React.useState(false);
  const [codingChoice, setCodingChoice] = useState({
    open: false,
    option: null,
    selectedId: null
  });

  const { content } = useContext(AppContext);
  // SnackContext defaults to null, and this modal is rendered in contexts
  // without a provider (e.g. its own RTL tests) — read it defensively so a
  // missing provider cannot crash the editor.
  const snackContext = useContext(SnackContext);
  const anonymous = isAnonymousOrLoading(content?.settings?.pedigreeSetting);
  const carrierStatusEnabled =
    content?.settings?.systemFeatures?.FEATURE_CARRIER_STATUS_IN_CONDITION ===
    true;

  // Flatten this person's genotype variants, keeping only P/LP ones — the
  // pedigree carrier-tested provenance links to a single P/LP variant. P/LP is
  // normalized-on-read here (display/link only; no variant entry this phase).
  const plpVariantOptions = useMemo(() => {
    if (!carrierStatusEnabled) return [];
    const out = [];
    for (const g of genotypes || []) {
      for (const v of g?.variants || []) {
        // No variantId → no usable variantRef, and undefined ids would conflate
        // options (undefined === undefined) in the picker. Exclude them.
        if (!v.variantId) continue;
        // An exact tier is required: the stored enum distinguishes pathogenic
        // from likely_pathogenic, so a compound classification
        // ("Pathogenic/Likely pathogenic") is deliberately not offered here
        // rather than being guessed into one of them.
        const tier = strictAcmgTier(leadingClassification(v)?.classification);
        if (tier !== 'Pathogenic' && tier !== 'Likely Pathogenic') continue;
        out.push({
          variantId: v.variantId,
          classification: TIER_TO_CLASSIFICATION[tier],
          label: `${v.gene || ''} ${
            v.variantP || v.variantC || v.variantId || ''
          }`.trim()
        });
      }
    }
    return out;
  }, [carrierStatusEnabled, genotypes]);

  const selectedTestedVariant = useMemo(
    () =>
      plpVariantOptions.find(
        (o) => o.variantId === formValues?.carrierBasis?.tested?.variantRef
      ) || null,
    [plpVariantOptions, formValues?.carrierBasis?.tested?.variantRef]
  );

  // A persisted tested link whose variant is no longer in the current P/LP set
  // (reclassified or deleted). The value is still stored — surface it read-only
  // rather than rendering a silently-blank field.
  const storedTested = formValues?.carrierBasis?.tested;
  const testedLinkStale = !!storedTested?.variantRef && !selectedTestedVariant;

  const handleAffectedStatusChange = (e) => {
    const value = e.target.value;
    setFormValues((prev) => {
      const next = { ...prev, affectedStatus: value };
      // Clearing carrierBasis when status moves away from carrier keeps the
      // record self-consistent (basis is meaningful only for carriers).
      if (value !== AFFECTED_STATUS.carrier) next.carrierBasis = undefined;
      return next;
    });
  };

  const handleTestedSelect = (event, option) => {
    setFormValues((prev) => {
      const carrierBasis = { ...(prev.carrierBasis || {}) };
      if (option) {
        carrierBasis.tested = {
          variantRef: option.variantId,
          classification: option.classification
        };
      } else {
        delete carrierBasis.tested;
      }
      return {
        ...prev,
        carrierBasis: Object.keys(carrierBasis).length
          ? carrierBasis
          : undefined
      };
    });
  };

  const handleObligateReasonChange = (e) => {
    const reason = e.target.value;
    setFormValues((prev) => {
      const carrierBasis = { ...(prev.carrierBasis || {}) };
      if (reason && reason.trim()) {
        // Obligate is only recorded once a reason is given; manual entry is
        // confirmed by definition.
        carrierBasis.obligate = { reason, source: 'manual', confirmed: true };
      } else {
        delete carrierBasis.obligate;
      }
      return {
        ...prev,
        carrierBasis: Object.keys(carrierBasis).length
          ? carrierBasis
          : undefined
      };
    });
  };

  // Reported = carrier known from an external/untracked test or clinical
  // knowledge. Recorded only once a note is given; coexists with tested/obligate.
  const handleReportedNoteChange = (e) => {
    const note = e.target.value;
    setFormValues((prev) => {
      const carrierBasis = { ...(prev.carrierBasis || {}) };
      if (note && note.trim()) {
        carrierBasis.reported = { ...(carrierBasis.reported || {}), note };
      } else {
        delete carrierBasis.reported;
      }
      return {
        ...prev,
        carrierBasis: Object.keys(carrierBasis).length
          ? carrierBasis
          : undefined
      };
    });
  };

  const handleReportedClassificationChange = (e) => {
    const value = e.target.value;
    setFormValues((prev) => {
      // Classification is meaningful only alongside a reported note.
      if (!prev.carrierBasis?.reported) return prev;
      return {
        ...prev,
        carrierBasis: {
          ...prev.carrierBasis,
          reported: { ...prev.carrierBasis.reported, classification: value }
        }
      };
    });
  };

  const computeAge = useCallback(
    (dateReported) => {
      if (!patientDob || !dateReported) return '';
      const parsedDate = new Date(dateReported);
      if (Number.isNaN(parsedDate.getTime())) return '';
      return calculateAge(patientDob, parsedDate);
    },
    [patientDob]
  );

  useEffect(() => {
    setFormValues({
      ...initialFormValues,
      recordedBy: initialFormValues?.recordedBy || currentUserName || ''
    });
  }, [initialFormValues, currentUserName]);

  useEffect(() => {
    // Anonymous pedigrees: age is the directly-edited source of truth — never
    // let a leftover dob+dateReported pair recompute over a typed age.
    if (anonymous || !patientDob) return;
    const nextAge = computeAge(formValues.dateReported);
    // Only apply a date-derived age — never erase the saved age just because
    // dateReported is empty. The inline-capture path stores age without a
    // date, and we must preserve it on re-hydration.
    if (nextAge === '') return;
    setFormValues((prev) => {
      if (String(nextAge) === String(prev.age ?? '')) return prev;
      return { ...prev, age: String(nextAge) };
    });
  }, [anonymous, patientDob, computeAge, formValues.dateReported]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await onSubmit({ ...formValues });
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await onDeleteDiagnose(formValues._id);
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const yesNoOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  // Re-point this record at a code. Only id+name are written: the search
  // option's own fields (codingSystems, sources, hasDuplicates) describe the
  // dropdown row, not the record, and spreading them would persist search
  // metadata onto the diagnosis. Colour is deliberately NOT set here — the
  // server re-derives it on a code change so the family keeps one colour per
  // disease and no colour shared by two (diagnoseController.updateDiagnoses).
  const applyDiseaseCode = (id, name) => {
    if (
      siblingDiagnoses.some(
        (d) => !sameCode(d._id, formValues?._id) && sameCode(d.id, id)
      )
    ) {
      snackContext?.setSnack?.({
        status: true,
        msg: `'${name}' with code ${id} is already recorded for this person.`,
        severity: 'info'
      });
      return;
    }
    setFormValues((pre) => ({ ...pre, id, name }));
  };

  const handleSelect = (event, value) => {
    if (!value) return;
    // More than one coding system for this name → let the clinician choose
    // which code to store, rather than silently taking the preferred one.
    // This is the path that makes re-coding possible at all: an ICD-10 record
    // can be moved to the same condition's OMIM code.
    if (value.codingSystems && value.codingSystems.length > 1) {
      setCodingChoice({
        open: true,
        option: value,
        selectedId: value.codingSystems[0].id
      });
      return;
    }
    applyDiseaseCode(value.id, value.name);
  };

  const closeCodingChoice = () =>
    setCodingChoice({ open: false, option: null, selectedId: null });

  const handleCodingChoiceConfirm = () => {
    const { option, selectedId } = codingChoice;
    closeCodingChoice();
    if (option && selectedId) applyDiseaseCode(selectedId, option.name);
  };

  const fetchDataByDisease = async (currentPage) => {
    if (searchTerm.length >= 3) {
      let url = getHPOSearchURL(
        searchTerm,
        currentPage,
        HPO_CONSTANTS.category['disease']
      );

      axios
        .get(url)
        .then(({ data }) => {
          setUnavailable(readTerminologyAvailability(data));

          const { results = [], totalCount } = data || {};

          const newOptions = results.map((d) => ({
            id: d.id,
            name: d.name,
            source: d.source,
            sources: d.sources || [],
            hasDuplicates: !!d.hasDuplicates,
            // Older backend responses only carry {id,name}; synthesise a
            // single-code coding system so the selection flow is uniform.
            codingSystems:
              d.codingSystems && d.codingSystems.length > 0
                ? d.codingSystems
                : [{ id: d.id, source: d.source, label: d.source }]
          }));

          if (!currentPage) {
            setOptions([...newOptions]);
          } else {
            setOptions((preOption) => [...preOption, ...newOptions]);
          }

          setTotalCount(totalCount);
          setPage(currentPage);
        })
        .catch((err) => {
          setUnavailable(terminologyAvailabilityFromError());
          setOptions([]);
          setTotalCount(null);
          console.error('Error fetching diseases:', err);
        });
    } else {
      setOptions([]);
    }
  };

  useEffect(() => {
    fetchDataByDisease(HPO_CONSTANTS['initialPage']);
    // eslint-disable-next-line
  }, [searchTerm]);

  const handleScroll = (event) => {
    const target = event.target;
    const margin = 1; // Margin to handle floating-point inconsistencies

    const scrollPosition =
      target.scrollHeight - target.scrollTop - target.clientHeight;
    const isEndOfScroll = Math.abs(scrollPosition) <= margin;
    const hasMoreData = options.length < totalCount;

    if (isEndOfScroll && hasMoreData) {
      let newCurrentPage = page + 1;
      fetchDataByDisease(newCurrentPage);
    }
  };

  const selectedItems = useMemo(() => {
    return formValues ? { id: formValues.id, name: formValues.name } : null;
  }, [formValues]);

  const handleCloseModal = () => {
    setOpen(false);
  };

  const handleOpenModal = () => {
    setOpen(true);
  };

  return (
    <form className="user-form" data-va-anchor="pedigree.diagnoses.editDialog">
      <ClinicalNoteDeleteModal
        loading={deleteLoading}
        open={open}
        handleCloseModal={handleCloseModal}
        onConfirm={handleDelete}
        title="Are you sure you want to delete this disease ?"
        content="Warning: This action will permanently delete this Disease. Once deleted, this
              information will be permanently removed and cannot be restored."
      />
      {codingChoice.open && (
        <CustomDialog
          title={'Choose coding system'}
          open={codingChoice.open}
          handleClose={closeCodingChoice}
          minHeight="auto"
          showActions={true}
          saveBtnLabel={'Use this code'}
          isParentHandlingLoading={true}
          onSave={handleCodingChoiceConfirm}
        >
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <strong>{codingChoice.option?.name}</strong> exists in more than
              one coding system. Select the code to record for this person:
            </Typography>
            <RadioGroup
              value={codingChoice.selectedId || ''}
              onChange={(e) =>
                setCodingChoice((prev) => ({
                  ...prev,
                  selectedId: e.target.value
                }))
              }
            >
              {(codingChoice.option?.codingSystems || []).map((cs, idx) => (
                <FormControlLabel
                  key={cs.id}
                  value={cs.id}
                  control={<Radio size="small" />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{cs.label}</span>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary'
                        }}
                      >
                        {cs.id}
                      </Typography>
                      {idx === 0 && (
                        <Chip
                          label={t('Preferred')}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 18, fontSize: 10 }}
                        />
                      )}
                      {sameCode(cs.id, formValues?.id) && (
                        <Chip
                          label={t('Current')}
                          size="small"
                          variant="outlined"
                          sx={{ height: 18, fontSize: 10 }}
                        />
                      )}
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </Box>
        </CustomDialog>
      )}
      <Grid
        container
        spacing={3}
        sx={{
          justifyContent: 'center'
        }}
      >
        {unavailable && (
          <Grid size={12}>
            <Alert severity="warning">
              {t(unavailable.message)}
              {unavailable.sources
                ? ` (${unavailable.sources.join(', ')})`
                : ''}
            </Alert>
          </Grid>
        )}
        <Grid size={12}>
          <Autocomplete
            clearOnBlur={false}
            id="tags-outlined"
            options={options}
            value={selectedItems}
            getOptionLabel={(option) => option.name}
            // The value is rebuilt from formValues, so it is never the same
            // object as an option — compare on code instead.
            isOptionEqualToValue={(option, value) =>
              sameCode(option?.id, value?.id)
            }
            onChange={handleSelect}
            filterOptions={(options, state) => options}
            // Same option rendering as the sidebar's disease search: source
            // chips appear only where one name exists in several coding
            // systems, so ordinary results stay uncluttered.
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                  }}
                >
                  <Typography variant="body1">{option.name}</Typography>
                  {option.hasDuplicates && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mt: 0.25,
                        flexWrap: 'wrap'
                      }}
                    >
                      {option.sources.map((s) => (
                        <Chip
                          key={s}
                          label={s}
                          size="small"
                          variant="outlined"
                          sx={{ height: 18, fontSize: 10 }}
                        />
                      ))}
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary'
                        }}
                      >
                        · choose coding system
                      </Typography>
                    </Box>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search for Diseases"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            )}
            slotProps={{
              listbox: {
                onScroll: handleScroll,
                sx: {
                  maxHeight: '280px',
                  overflowY: 'auto'
                }
              }
            }}
          />
          {/* The code being edited. Two records can share a name and differ
              only by coding system, so without this the clinician cannot tell
              which of them this dialog is editing. */}
          {formValues?.id && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.75,
                flexWrap: 'wrap'
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary'
                }}
              >
                {t('Code')}: {formValues.id}
              </Typography>
              {!sameCode(initialFormValues?.id, formValues.id) && (
                <Chip
                  label={`${t('was')} ${initialFormValues?.id}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: 18, fontSize: 10 }}
                />
              )}
            </Box>
          )}
        </Grid>
        <Grid size={6}>
          <TextInput
            label={t('Pedigree Display Name')}
            name="alias"
            placeholder={t('Enter display name')}
            value={formValues?.alias}
            onChange={handleChange}
          />
        </Grid>
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}
          size={6}
        >
          <FormLabel>{t('Select Colour')}</FormLabel>
          <ColorPicker color={formValues?.color} onChange={handleChange} />
        </Grid>
        <Grid size={6}>
          <TextInput
            label={t('Diagnosis Age')}
            name="age"
            type="number"
            value={formValues?.age ?? ''}
            // Disabled (muted) only when an age is actually being
            // auto-calculated, i.e. DoB AND date reported both set.
            // Editable when either is missing so a known age can be
            // recorded directly. Anonymous mode is always direct entry.
            disabled={!anonymous && !!patientDob && !!formValues?.dateReported}
            onChange={
              !anonymous && patientDob && formValues?.dateReported
                ? undefined
                : (e) => {
                    const val = e.target.value;
                    const parsed = val === '' ? '' : Number(val);
                    if (val === '' || !Number.isNaN(parsed)) {
                      setFormValues((prev) => ({
                        ...prev,
                        age: val === '' ? '' : String(parsed)
                      }));
                    }
                  }
            }
          />
        </Grid>
        {!anonymous && (
          <Grid size={6}>
            <TextInput
              type="date"
              label={t('Diagnosis Date')}
              name="dateReported"
              value={formValues?.dateReported}
              onChange={(date) => {
                setFormValues((prev) => ({
                  ...prev,
                  dateReported: date,
                  age:
                    computeAge(date) === ''
                      ? prev.age
                      : String(computeAge(date))
                }));
              }}
              disableFuture
            />
          </Grid>
        )}
        <Grid size={12}>
          <TextInput
            label={t('Recorded By')}
            name="recordedBy"
            type="autocomplete"
            value={formValues?.recordedBy || ''}
            options={staffMemberNames}
            onChange={(newValue) => {
              const label = newValue ? newValue.label || newValue : '';
              handleChange({ target: { name: 'recordedBy', value: label } });
            }}
          />
        </Grid>
        <Grid id="top" size={carrierStatusEnabled ? 6 : 12}>
          <FormControl component="fieldset" fullWidth>
            <Grid container spacing={1}>
              <Grid>
                <FormLabel>Mode of Inheritance </FormLabel>
              </Grid>
              <Grid size={12}>
                <SelectInput
                  fullWidth={true}
                  variant="outlined"
                  name="inheritanceMode"
                  options={CONSTANTS.inheritanceModesOptions}
                  value={formValues.inheritanceMode || ''}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          </FormControl>
        </Grid>
        {carrierStatusEnabled && (
          <Grid size={6}>
            <FormControl component="fieldset" fullWidth>
              <Grid container spacing={1}>
                <Grid>
                  <FormLabel>{t('Affected Status')}</FormLabel>
                </Grid>
                <Grid size={12}>
                  <SelectInput
                    fullWidth={true}
                    variant="outlined"
                    name="affectedStatus"
                    options={AFFECTED_STATUS_OPTIONS}
                    value={formValues?.affectedStatus ?? ''}
                    onChange={handleAffectedStatusChange}
                  />
                </Grid>
              </Grid>
            </FormControl>
          </Grid>
        )}
        <Grid size={6}>
          <RadioInput
            label={t('Clinically Confirmed')}
            name="clinicallyConfirmed"
            onChange={handleChange}
            options={yesNoOptions}
            value={formValues?.clinicallyConfirmed}
            row
          ></RadioInput>
        </Grid>
        <Grid size={6}>
          <RadioInput
            label={t('Patient Reported')}
            name="isPatientReported"
            onChange={handleChange}
            options={yesNoOptions}
            value={formValues?.isPatientReported}
            row
          ></RadioInput>
        </Grid>
        <Grid size={6}>
          <RadioInput
            label={t('Private')}
            name="isPrivate"
            value={formValues?.isPrivate}
            onChange={handleChange}
            options={yesNoOptions}
            row
          ></RadioInput>
        </Grid>
        <Grid size={6}>
          <RadioInput
            label={t('Display on Pedigree')}
            name="displayOnPedigree"
            value={formValues?.displayOnPedigree}
            onChange={handleChange}
            options={yesNoOptions}
            row
          ></RadioInput>
        </Grid>

        {carrierStatusEnabled &&
          formValues?.affectedStatus === AFFECTED_STATUS.carrier && (
            <Grid size={12}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <FormLabel>{t('Tested')}</FormLabel>
                  {plpVariantOptions.length ? (
                    <Autocomplete
                      options={plpVariantOptions}
                      value={selectedTestedVariant}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) =>
                        option.variantId === value.variantId
                      }
                      onChange={handleTestedSelect}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('Pathogenic / likely-pathogenic variant')}
                          placeholder={t('Select a variant')}
                        />
                      )}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary'
                      }}
                    >
                      {t('No pathogenic / likely-pathogenic variants recorded')}
                    </Typography>
                  )}
                  {testedLinkStale && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary'
                      }}
                    >
                      {t('Linked variant')}: {storedTested.variantRef}
                      {storedTested.classification
                        ? ` (${storedTested.classification})`
                        : ''}
                    </Typography>
                  )}
                </Grid>
                <Grid size={12}>
                  <FormLabel>
                    {t('Reported / clinically-known carrier')}
                  </FormLabel>
                  <TextInput
                    label={t('Reported carrier note')}
                    name="reportedNote"
                    placeholder={t(
                      'e.g. carrier on external test (lab / date), not recorded in TrakGene'
                    )}
                    value={formValues?.carrierBasis?.reported?.note || ''}
                    onChange={handleReportedNoteChange}
                    required
                    multiline
                    rows={2}
                    fullWidth
                  />
                  {formValues?.carrierBasis?.reported && (
                    <>
                      <SelectInput
                        fullWidth
                        variant="outlined"
                        name="reportedClassification"
                        options={REPORTED_CLASSIFICATION_OPTIONS}
                        value={
                          formValues?.carrierBasis?.reported?.classification ||
                          ''
                        }
                        onChange={handleReportedClassificationChange}
                      />
                      <ProvenancePill>
                        {t('Reported · external/clinical')}
                      </ProvenancePill>
                    </>
                  )}
                </Grid>
                <Grid size={12}>
                  <TextInput
                    label={t('Obligate Reason')}
                    name="obligateReason"
                    placeholder={t('Reason for obligate carrier designation')}
                    value={formValues?.carrierBasis?.obligate?.reason || ''}
                    onChange={handleObligateReasonChange}
                    required
                    multiline
                    rows={2}
                    fullWidth
                  />
                  {formValues?.carrierBasis?.obligate && (
                    <ProvenancePill>{t('Obligate · pedigree')}</ProvenancePill>
                  )}
                </Grid>
              </Grid>
            </Grid>
          )}

        <Grid size={6}>
          <TextInput
            label={t('Karyotype')}
            name="karyotype"
            placeholder={t('e.g. 47,XX,+21 or 47,XX,+21[12]/46,XX[28]')}
            value={formValues?.karyotype || ''}
            onChange={handleChange}
            fullWidth
          />
        </Grid>
        <Grid size={6}>
          <RadioInput
            label={t('Mosaicism')}
            name="mosaicism"
            onChange={handleChange}
            options={yesNoOptions}
            value={formValues?.mosaicism}
            row
          />
        </Grid>
        <Grid size={12}>
          <TextInput
            label={t('Comment')}
            name="comment"
            placeholder={t('Enter comment')}
            value={formValues?.comment || ''}
            onChange={handleChange}
            multiline
            rows={3}
            fullWidth
          />
        </Grid>
        <Grid size={12}>
          <Divider />
        </Grid>
        <ButtonContainer size={12}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleOpenModal}
            disabled={isDisabled || deleteLoading}
            endIcon={deleteLoading ? <CircularProgress size={15} /> : undefined}
          >
            {t('Delete')}
          </Button>
          <Button variant="outlined" color="secondary" onClick={handleClose}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSave}
            disabled={isDisabled || loading || deleteLoading}
            endIcon={loading ? <CircularProgress size={15} /> : undefined}
          >
            {t('Save')}
          </Button>
        </ButtonContainer>
      </Grid>
    </form>
  );
};

export default EditDiagnoseModal;
